import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ContentBox } from '../layout/MainBox';
import { FormProvider } from './FormContext';
import { FormFieldGroupContainer } from './FormFieldGroupContainer';
import { DynamicFormField } from './formFields/DynamicFormField';
import { FormSection } from './FormSection';

/**
 * A standard Form in the Kernel UI
 * 
 * This Component is responsible for managing the state of the form (values, errorState, and mode)
 * The state is provided to child FormField components via FormContext
 * 
 * FormFields are expected to interact with the FormContext to assign values and errors, as well as
 * manage their disabled state depending on mode
 * 
 * This component can be leveraged in two ways:
 * 1. If fieldDefs are provided, the Form will render them as DynamicFormFields
 * 2. FormField components that are built to interact with FormContext can be provided as children in any layout in the HTML
 */
export const Form = ({
  fieldDefs, // Optional JSON representation of the fields to be rendered in the form. If not provided, children will be rendered instead
  useSections = false, // Meant to be used in conjunction with fieldDefs if they are provided
  container, // If provided, the DynamicFormFields will be rendered in this container. This only works if useSections = false
  children,

  formValues: formValuesProp,
  setFormValues: setFormValuesProp,
  ref
}) => {

  // State
  const [internalFormValues, setInternalFormValues] = useState({});
  const [errorStates, setErrorStates] = useState({});
  const [touchedStates, setTouchedStates] = useState({});

  const formValues = formValuesProp ?? internalFormValues;
  const setFormValues = setFormValuesProp ?? setInternalFormValues;

  // API exposed to the Parent
  useImperativeHandle(ref, () => {
    return {
      formValues,
      setFormValues,
      assignFormValues: (newValues) => {
        setFormValues(prevValues => ({ ...prevValues, ...newValues }))
      },
      hasErrorState: () => {
        return Object.values(errorStates).some(value => value !== null && value !== undefined);
      },
      touchAll: () => {
        setTouchedStates(prev => Object.fromEntries(Object.keys(prev).map(key => [key, true])))
      }
    }
  }, [formValues, errorStates, setFormValues])

  // Cleanup Queue - Used by FormFields to cleanup if they unmount.
  // This pattern is necessary to prevent the formValues from being cleared if this Form is in controlled
  // mode and the entire Form is unmounting. This works by processing the cleanupQueue on the subsequent render cycle.
  // If the entire Form unmounts, the queue will be gone before the next render cycle can happen
  const cleanupQueueRef = useRef([]);
  const [cleanupTick, setCleanupTick] = useState(0);

  const enqueueCleanup = (entry) => {
    cleanupQueueRef.current.push(entry);
    setCleanupTick((tick) => tick + 1);
  };

  useEffect(() => {
    if (cleanupQueueRef.current.length === 0) return;

    const entries = cleanupQueueRef.current;
    cleanupQueueRef.current = [];

    setFormValues(prevValues => {
      const nextValues = { ...prevValues };
      entries.forEach(({ valueKeys }) => {
        valueKeys.forEach(valueKey => {
          nextValues[valueKey] = null;
        });
      });
      return nextValues;
    });

    setErrorStates(prev => {
      const next = { ...prev };
      entries.forEach(({ field }) => {
        next[field] = null;
      });
      return next;
    });

    setTouchedStates(prev => {
      const next = { ...prev };
      entries.forEach(({ field }) => {
        next[field] = null;
      });
      return next;
    });
  }, [cleanupTick]);

  return (
    <FormProvider
      formValues={formValues}
      setFormValues={setFormValues}
      errorStates={errorStates}
      setErrorStates={setErrorStates}
      touchedStates={touchedStates}
      setTouchedStates={setTouchedStates}
      enqueueCleanup={enqueueCleanup}>
      {fieldDefs ? renderFieldDefs(fieldDefs, useSections, container) : children}
    </FormProvider>
  )
}

const renderFieldDefs = (fieldDefs, useSections, container) => {
  if (!useSections) {
    // Plain FormFields without sections
    const dynamicFormFields = fieldDefsToDynamicFormFields(fieldDefs);
    const ContainerComponent = container ?? FormFieldGroupContainer
    return (
      <ContainerComponent>
        {dynamicFormFields}
      </ContainerComponent>
    )
  }

  // FormFields will be grouped into sections
  const sections = [];
  const sectionToFieldDefs = {};
  fieldDefs.forEach(fieldDef => {
    const section = fieldDef.section;
    if (!sections.includes(section)) {
      sections.push(section);
    }

    let sectionFieldDefs = sectionToFieldDefs[section];
    if (!sectionFieldDefs) {
      sectionFieldDefs = [];
      sectionToFieldDefs[section] = sectionFieldDefs;
    }
    sectionFieldDefs.push(fieldDef);
  })

  // Audit always comes last
  sections.sort((sectionA, sectionB) =>
    (sectionA === 'Audit') - (sectionB === 'Audit')
  );

  return (
    <ContentBox sx={{ overflow: 'auto'}}>
    {sections.map(section =>
    <FormSection key={section} title={section}>
      <FormFieldGroupContainer>
        {fieldDefsToDynamicFormFields(sectionToFieldDefs[section])}
      </FormFieldGroupContainer>
    </FormSection>
  )}
  </ContentBox>)
}

const fieldDefsToDynamicFormFields = (fieldDefs) => {
  return fieldDefs.map(fieldDef => {
    if (fieldDef.hide) return null;
    return fieldDef.component ?? <DynamicFormField key={fieldDef.field} fieldDef={fieldDef} />;
  });
}